import { Button } from "@/components/ui/button";
import { FieldLabel, Input } from "@/components/ui/input";
import { ErrorNote } from "@/components/ui/panel";

/**
 * The bulk toolbar for Review's queue — DESIGN §7.5.
 *
 * Approve/reject/requeue are single-question endpoints (ARCHITECTURE §8 rule 3: every
 * mutation goes through `orchestration/`, keyed on one row, for the audit trail). This
 * bar fans a decision out to every checked row instead of waiting on a batch endpoint
 * that does not exist — clearing a page of near-duplicates one at a time is exactly
 * the round-trip cost this queue already fixed for single rows.
 */
export function BulkActionBar({
  count,
  busy,
  note,
  onNoteChange,
  onApprove,
  onReject,
  onRequeue,
  onReenrich,
  onClear,
  error,
}: {
  count: number;
  busy: boolean;
  note: string;
  onNoteChange: (value: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onRequeue: () => void;
  onReenrich: () => void;
  onClear: () => void;
  error?: unknown;
}) {
  return (
    <div className="border-bank-border bg-bank-accent-wash space-y-2 border-b px-3.5 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-bank-accent-ink font-mono text-[11px] font-medium tabular-nums">
          {count} selected
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="primary" disabled={busy} onClick={onApprove}>
            Approve
          </Button>
          <Button size="sm" variant="danger" disabled={busy} onClick={onReject}>
            Reject
          </Button>
          <Button size="sm" disabled={busy} onClick={onRequeue}>
            Requeue
          </Button>
          <Button
            size="sm"
            disabled={busy}
            title="Force a fresh enrichment pass for every selected row"
            onClick={onReenrich}
          >
            Re-enrich
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={onClear}>
            Clear
          </Button>
        </div>
      </div>
      <div>
        <FieldLabel htmlFor="bulk-note">
          Note — recorded on every status_transitions row this creates (§7.5)
        </FieldLabel>
        <Input
          id="bulk-note"
          value={note}
          placeholder="why, for whoever reads this in six months"
          onChange={(e) => onNoteChange(e.target.value)}
        />
      </div>
      <ErrorNote error={error} />
    </div>
  );
}
